/**
 * PersonProfile.tsx — one colleague's profile, as `/people/:userId` (`DS-F21`, `PR-F03`).
 *
 * The page a person suggestion in the global search field opens. It renders ONLY what
 * `REQUIREMENTS_PROFILE.md` §3.2 makes public to every authenticated employee, and it can render no
 * more than that because `GET /api/people/:userId` never returns anything else — the enforcement is in
 * the query, not in this component (`PR-F05`: "hiding it in the interface is not sufficient").
 *
 * WHY NOT REUSE `Profile.tsx`: that page is the OWNER's view throughout — it shows sign-in name, email
 * and last-seen, offers avatar and bio editors, and renders "only you can see this" tool lists. Adding
 * a viewer mode to it would put the public and private renderings one boolean apart. So this is a
 * separate, deliberately small component that reuses only the presentational helpers (`monogram`,
 * `Pills`, `PROFILE_STYLES`) and shares its visual language.
 *
 * `Field` is the ONE helper it no longer takes from `Profile.tsx`: that copy renders a visible "Not
 * provided by the directory" note for every empty value, which is the opposite of what `UR-F04`
 * requires. It now comes from `./fields`, which hides an empty row — see that file for why the fix
 * belongs in the helper.
 *
 * LABELS ARE REQUIREMENTS HERE, not decoration: skills and certifications must read "self-declared"
 * (`UR-F31`, `PR-F49`) and a competency level must read "self-assessed" (`PR-F25`, `UR-F16`) — they are
 * a person's own claims and must never look like something the platform verified.
 *
 * THE ONE FIGURE ON THIS PAGE is the follower count (`PR-F02`/`PR-F52`), which §3.2 puts in the public
 * class as a bare number — never WHO follows them (that is shown to nobody), never sorted, never
 * compared. NOTHING ELSE HERE IS A NUMBER (`PC-F31`): no badge count, no adoption count, no leaderboard
 * position. A marker is a label a person holds, never a figure.
 */
import { useState } from 'react';
import { Link, useLoaderData, type LoaderFunctionArgs } from 'react-router';
import { AVATAR_DATA_URI_RE } from '@trailblazer/shared';
import { fetchPerson, type PublicPerson, type PublicPersonBadge } from '../search/api';
// `CONTRIBUTION_FIGURE_LABEL`/`LEARNING_STEP_LABEL`/`contributionFigureValueText` come from `Profile.tsx`
// with the rest of the shared profile vocabulary: they are the ONE client-side naming of the closed six
// figures and the closed seven training steps, so this page and the owner's own choosers can never call the
// same thing two different things. LEARNING_STEP_LABEL is also why no page prints the server's own step
// titles — those adapt by level (see its comment), so publishing one would disclose the person's
// self-declared competency, which UR-F18/PR-F12 let them hide.
import {
  CONTRIBUTION_FIGURE_LABEL,
  LEARNING_STEP_LABEL,
  Pills,
  PROFILE_STYLES,
  STOCK_AVATARS,
  contributionFigureValueText,
  monogram,
} from '../Profile';
import { SignedOut } from '../tools/shared';
import { CommunityLabels, Field } from './fields';
import { fetchSupportChampion, type SupportChampion } from './api';
import { SupportChampionCard } from './SupportChampionCard';

/** How many badges show before "View more" — the FEATURED set (mirrors the server's FEATURED_BADGES).
 *  Presentation only; NEVER surfaced as a count (PC-F31). */
const FEATURED_BADGES = 3;

/**
 * Every badge the person has EARNED and NOT hidden (PR-F43, shown-by-default 0032), following the
 * W3-people credentials pattern: the featured few (masterlist-ordered, so the first FEATURED_BADGES)
 * show first, and "View more" reveals the rest INLINE — no navigation, no fetch (the whole set already
 * loaded with the profile). Reuses the SAME `.tb-profile__achievements` accent chips the owner's own
 * /me strip uses. NEVER a count (PC-F31): the button is a bare "View more", and the section is omitted
 * entirely when empty (an empty heading would advertise that something was hidden), like the About one.
 */
function PersonBadges({ badges }: { badges: PublicPersonBadge[] }) {
  const [expanded, setExpanded] = useState(false);
  if (badges.length === 0) return null;
  const hasMore = badges.length > FEATURED_BADGES;
  const shown = expanded || !hasMore ? badges : badges.slice(0, FEATURED_BADGES);
  return (
    <section aria-labelledby="person-badges">
      <h2 id="person-badges">Badges</h2>
      <ul className="tb-profile__achievements">
        {shown.map((b) => (
          <li key={b.badgeKey} title={b.criterionText}>
            {b.name}
          </li>
        ))}
      </ul>
      {hasMore && (
        <button
          type="button"
          className="tb-profile__viewmore"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'View fewer' : 'View more'}
        </button>
      )}
    </section>
  );
}

/**
 * The profile as `GET /api/people/:userId` now returns it, plus the three §3.2-public fields the shared
 * mirror in `search/api.ts` has not caught up with: the follower count (`PR-F02`/`PR-F52`), the chosen
 * contribution figures and the chosen learning.
 *
 * WHY THEY ARE STILL DECLARED HERE rather than in that mirror, unlike the equivalents on the other public
 * profile: `web/src/search/**` is ANOTHER STREAM'S EXCLUSIVE TERRITORY (`DEMO_WORKSTREAMS.md`), not merely
 * a file someone happened to have open, so this is a standing hand-off and not a temporary one. The
 * counterpart mirror for `/api/users/:userId` lives in `web/src/community/api.ts`, which IS this stream's,
 * and the same three fields were folded straight into it — so do not read this local declaration as the
 * house pattern. It is what strict territory costs.
 *
 * ALL THREE ARE OPTIONAL because a response is untrusted data and this is a hand-written mirror: an API
 * that omits one must leave that part of the page off rather than render "NaN followers" or throw on
 * `undefined.filter`.
 */
type PublicPersonView = PublicPerson & {
  followerCount?: number;
  contributionFigures?: PublicPersonContributionFigure[];
  learningSteps?: PublicPersonLearningStep[];
};

/**
 * One contribution figure this person CHOSE to publish (`UR-F21`/`PR-F27`/`PR-F56`), mirroring backend
 * `db/profile.ts` `PublicContributionFigure`. Declared here for the same reason `followerCount` is: it
 * belongs in `PublicPerson` in `search/api.ts`, which is another stream's exclusive territory — so it is
 * declared beside its consumer and handed over rather than edited across territories.
 *
 * `figure` is a plain string, not a union: the server owns the closed set of six (0012's CHECK), and a
 * second client-side list of them would be free to drift. The renderer looks the name up in the shared
 * `CONTRIBUTION_FIGURE_LABEL` map and skips anything it does not know.
 *
 * AN UNCHOSEN FIGURE IS ABSENT from the array, and nothing says how many were withheld (`PR-F05`).
 */
interface PublicPersonContributionFigure {
  figure: string;
  value: number;
}

/**
 * One completed training step this person CHOSE to publish (`PR-F30`), mirroring backend `db/profile.ts`
 * `PublicLearningStep`. Declared here for the same reason `followerCount` and the figure above are: it
 * belongs in `PublicPerson` in `search/api.ts`, which is another stream's exclusive territory.
 *
 * A hand-written mirror missing a field drops it silently with both typechecks green, so this is the WHOLE
 * server shape: the step number, and nothing else. No `shown` flag — an unchosen step is ABSENT from the
 * array (`PR-F05`) — and no completion date, which would turn finished lessons into an activity timeline.
 */
interface PublicPersonLearningStep {
  stepNo: number;
}

/**
 * The figures THIS PERSON chose to show, from the closed set of six — three by default, the rest by
 * their own choice (`UR-F21`/`UR-F33`/`PR-F56`).
 *
 * THE SERVER DECIDED WHAT IS IN HERE. An unchosen figure is absent from the array rather than present
 * with a flag, so there is no `shown` test on this page to get wrong and no withheld value anywhere in
 * the payload it renders from — `PR-F05`: hiding it in the interface is not sufficient.
 *
 * OMITTED WHEN EMPTY, like `PersonBadges` and About above: someone may turn all six off, and a bare
 * "Contributions" heading over nothing would itself announce that something had been hidden. A missing
 * field renders identically to `[]`, on purpose — this is a hand-written mirror, so an API that has not
 * caught up must leave the section off rather than throw.
 *
 * COUNTS ONLY, NEVER NAMES: `total_adoptions` is a number and says nothing about WHO adopted — those
 * people published nothing on this profile. NO total, NO score, NO rank, no comparison with anybody
 * (`UR-F22`/`PR-F58`/`PC-F31`); six independent facts, in the order the server sent them.
 */
function PersonContributionFigures({ figures }: { figures: PublicPersonContributionFigure[] }) {
  // A figure name this build does not recognise is skipped, not rendered: the closed set is the
  // server's, and a raw column name is not a label to put on somebody's public profile.
  const known = figures.filter((f) => CONTRIBUTION_FIGURE_LABEL[f.figure] !== undefined);
  if (known.length === 0) return null;
  return (
    <section aria-labelledby="person-contributions">
      <h2 id="person-contributions">Contributions</h2>
      {/* A plain name-and-number list (`.tb-figures`), never a bar or a share of a total: a figure here
          is a fact about one person, not a position among people (PC-F31). The `div` wrapper per pair is
          what lets each figure stack its label over its value. */}
      <dl className="tb-figures">
        {known.map((f) => (
          <div key={f.figure}>
            <dt>{CONTRIBUTION_FIGURE_LABEL[f.figure]}</dt>
            <dd>{contributionFigureValueText(f.figure, f.value)}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/**
 * The completed training steps THIS PERSON chose to show (`PR-F30`).
 *
 * THE SERVER DECIDED WHAT IS IN HERE: a step appears only if they completed it AND chose to publish it, so
 * this page has no `shown` test to get wrong and nothing in the payload about a step they withheld
 * (`PR-F05`). Nothing says how many were withheld, or which.
 *
 * OMITTED WHEN EMPTY, like `PersonBadges`, the figures and About — and empty is the DEFAULT here, not the
 * edge case: nothing learning-related is public until the person opts a step in, so most profiles carry no
 * such section at all. A "Learning" heading over nothing would announce that something had been hidden.
 *
 * NO "n OF 7", NO PROGRESS RING, NO SCORE (`PC-F31`) — the file header's rule that nothing on this page is
 * a number applies here more sharply than anywhere: a count of shown steps is the arithmetic of the
 * person's own opt-out, published back at them. A list of what they chose to say, and nothing more.
 */
function PersonChosenLearning({ steps }: { steps: PublicPersonLearningStep[] }) {
  // A step number this build cannot name is skipped, not rendered: the closed set is the server's.
  const known = steps.filter((s) => LEARNING_STEP_LABEL[s.stepNo] !== undefined);
  if (known.length === 0) return null;
  return (
    <section aria-labelledby="person-learning">
      <h2 id="person-learning">Learning they&apos;ve completed</h2>
      <ul>
        {known.map((s) => (
          <li key={s.stepNo}>{LEARNING_STEP_LABEL[s.stepNo]}</li>
        ))}
      </ul>
    </section>
  );
}

/** A follower count only when the response actually carried a sane one; else null → show nothing. */
function followerCountOf(person: PublicPersonView): number | null {
  const n = person.followerCount;
  return typeof n === 'number' && Number.isFinite(n) && n >= 0 ? Math.floor(n) : null;
}

export type PersonLoaderData =
  | {
      state: 'ok';
      person: PublicPersonView;
      /** The champion for THEIR department (`UR-F10`); null when there is none — render nothing. */
      champion: SupportChampion | null;
    }
  | { state: 'signedOut' }
  | { state: 'notFound' };

export async function personLoader({ params }: LoaderFunctionArgs): Promise<PersonLoaderData> {
  const userId = params.userId ?? '';
  if (!userId) return { state: 'notFound' };
  const res = await fetchPerson(userId);
  if (res.kind === 'signedOut') return { state: 'signedOut' };
  if (res.kind === 'notFound') return { state: 'notFound' };
  // The champion is a SEPARATE sub-resource (see ./api for why) so it is fetched only once the person
  // is known to be viewable — a departed or unknown id has already returned above. It cannot fail the
  // page: fetchSupportChampion collapses every failure to null and the card then renders nothing.
  return { state: 'ok', person: res.person, champion: await fetchSupportChampion(userId) };
}

/**
 * Resolve the avatar to display, applying the same allowlist the owner's page does.
 *
 * SECURITY: this is the render chokepoint. A `stock:` token is mapped through the bundled allowlist and
 * the token itself is NEVER emitted; an uploaded ref reaches `<img src>` only if it matches
 * `AVATAR_DATA_URI_RE`. Anything else falls through to the directory photo, then to a monogram — so a
 * hostile stored value cannot become a script, a remote fetch or a tracking pixel.
 */
function avatarUrl(person: PublicPerson): string | null {
  const ref = person.avatarRef;
  if (person.avatarKind === 'stock' && typeof ref === 'string' && ref.startsWith('stock:')) {
    const hit = STOCK_AVATARS.get(ref.slice('stock:'.length));
    if (hit) return hit.url;
  }
  if (person.avatarKind === 'upload' && typeof ref === 'string' && AVATAR_DATA_URI_RE.test(ref)) {
    return ref;
  }
  // The directory photo is also untrusted TEXT, so it passes the same test.
  if (typeof person.photoRef === 'string' && AVATAR_DATA_URI_RE.test(person.photoRef)) {
    return person.photoRef;
  }
  return null;
}

export function PersonProfile() {
  const data = useLoaderData() as PersonLoaderData;

  if (data.state === 'signedOut') return <SignedOut title="Profile" returnTo="/" />;
  if (data.state === 'notFound') {
    return (
      <section aria-labelledby="person-missing">
        <h1 id="person-missing">Profile not available</h1>
        {/*
          Deliberately one message for "no such person" AND "they have left" — the backend conflates the
          two so ids cannot be probed to enumerate former colleagues, and this copy must not undo that
          by hinting which case it is.
        */}
        <p className="tb-profile__note">
          This profile is not available. The colleague may have left the organisation.
        </p>
        <p>
          <Link to="/tools">Back to the catalogue</Link>
        </p>
      </section>
    );
  }

  const person = data.person;
  const img = avatarUrl(person);
  const self = person.selfDeclared;
  const followers = followerCountOf(person);
  const orgLine = [person.jobTitle, person.department].filter(Boolean).join(' · ');
  // UR-F04 carried up one level: with every empty row now hidden, a wholly sparse account would leave
  // an "Organisation" heading over an empty list — the same visible hole the requirement closes, and
  // what PR-F31 means by omitting an empty section. So the section itself goes when nothing fills it.
  const hasOrg = [
    person.jobTitle,
    person.department,
    person.officeLocation,
    person.team,
    person.roleLabel,
  ].some((v) => typeof v === 'string' && v.trim() !== '');
  // PR-F31 / PR-NFR09: omit an EMPTY About section rather than raise a heading over nothing.
  //
  // A NON-NULL `selfDeclared` STILL DOES NOT MEAN THERE IS ANYTHING WORTH RENDERING, so this gates on
  // CONTENT rather than on the block's presence. History, because it explains why the check is this
  // thorough: `db/publicProfile.ts` reaches `app.user_self_declared` by LEFT JOIN, and it used to return
  // the block whenever the section was not hidden — so a person with no row at all got
  // `{bio: null, interests: [], …, certifications: []}` rather than null. That was the MAJORITY case,
  // not an edge one (`seed/people.ts` writes the row for 18 of 60 personas), and this page rendered
  // `self ? …`, producing for the other 42 a bare "About" heading with the self-declared disclaimer
  // beneath it and no content — which PR-NFR09 forbids ("a profile with nothing filled in must not look
  // like a failure") and which reads as though something had been HIDDEN when nothing was ever set.
  //
  // The server now collapses that all-empty case to null itself, so hidden and never-declared are one
  // answer there too. This check is NOT thereby redundant: it is a field stricter. WHITESPACE counts as
  // empty here, exactly as `Field` in ./fields treats it — a bio of `" "` is not an answer, but it is not
  // `null` either, so the server keeps the block alive and only this gate closes the visible hole.
  const hasSelf =
    self != null &&
    ((self.bio ?? '').trim() !== '' ||
      self.interests.length > 0 ||
      self.skillsHave.length > 0 ||
      self.skillsWanted.length > 0 ||
      self.competencyLevel != null ||
      self.makerType != null ||
      // A person whose ONLY self-declared content is a certification still gets an About section:
      // UR-F32/PR-F49 make it §3.2-public and PR-F12 puts it inside the hideable block, so it belongs
      // here rather than in a section of its own.
      self.certifications.length > 0);

  return (
    <section aria-labelledby="person-heading" className="tb-profile">
      <style>{PROFILE_STYLES}</style>

      <header className="tb-profile__head">
        {img ? (
          <img className="tb-avatar" src={img} alt={`${person.name}'s avatar`} />
        ) : (
          // PR-F14: a neutral placeholder, never an empty frame.
          <span className="tb-avatar tb-avatar--fallback" aria-hidden="true">
            {monogram(person.name)}
          </span>
        )}
        <div>
          <h1 id="person-heading">{person.name}</h1>
          {orgLine ? <p className="tb-profile__note">{orgLine}</p> : null}
          {/* Champion / SWAT only, resolved from a live appointment (PR-F24). Never a count. */}
          {person.markers.length > 0 ? (
            // Reuses the existing badge-chip styling rather than introducing a parallel one. A marker is
            // a label the person holds (PR-F24); it never carries a count (PC-F31).
            <ul className="tb-profile__badges">
              {person.markers.map((m) => (
                <li key={m}>{m === 'SWAT' ? 'SWAT team' : 'Champion'}</li>
              ))}
            </ul>
          ) : null}
          {/* PR-F02 / PR-F52: §3.2 makes the follower COUNT public, so it belongs on a colleague's
              profile — as a bare number, never who follows them, and never something this page orders
              anybody by (PC-F31). The follow/unfollow control itself lives on /users/:userId (PR-F51);
              this page states the count only. Omitted when the response carried no usable one. */}
          {followers !== null ? (
            <p className="tb-profile__note">
              {followers === 1 ? '1 follower' : `${followers} followers`}
            </p>
          ) : null}
        </div>
      </header>

      {hasOrg ? (
        <section aria-labelledby="person-org">
          <h2 id="person-org">Organisation</h2>
          {/*
            Every row below is omitted when the directory has no value for it (UR-F04 — "blank is an
            accurate answer"), so a sparse account shows a short list rather than rows of "not
            provided". See ./fields.
          */}
          <dl>
            <Field label="Job title" value={person.jobTitle} />
            <Field label="Department" value={person.department} />
            <Field label="Office" value={person.officeLocation} />
          </dl>
          {/*
            Team and RoleLabel are DISPLAY ONLY (UR-F34): shown here, and read by nothing else — not a
            filter, not a metric, not an ordering, and not a search key. People search deliberately does
            not match on them.

            AND SHOWN AS A SUBORDINATE GROUP, not as two more rows of the list above (PR-F50, closed with
            PR-F02): "next to, and visibly subordinate to, the job title and department from the
            directory, so a reader can tell which is the organisation's record and which is a community
            label". Equal-weight rows in the same <dl> made all five look like the directory's. The group
            is the same shared component UserProfile.tsx uses, so the two public profiles cannot drift
            apart on the distinction — see ./fields.
          */}
          <CommunityLabels team={person.team} roleLabel={person.roleLabel} />
        </section>
      ) : null}

      {/*
        The department's support champion (UR-F10, PR-F21/F22/F26) — a contact, not a decoration.
        Sits directly under Organisation because it is a fact about the DEPARTMENT named there, and
        renders nothing at all when the department has no champion (UR-F11/PR-F23).
      */}
      <SupportChampionCard champion={data.champion} headingId="person-champion" />

      {/*
        The self-declared section renders ONLY when the person has not hidden it (UR-F18, PR-F12) AND
        actually declared something (PR-F31 — see `hasSelf` above for why those are two different tests).
        The API returns null for the hidden case, so nothing here has to decide THAT — and crucially, an
        absent or empty section renders NOTHING rather than an empty heading, because a heading over
        nothing would itself advertise that something was hidden.
      */}
      {hasSelf && self ? (
        <section aria-labelledby="person-self">
          <h2 id="person-self">About</h2>
          <p className="tb-profile__note">
            Self-declared by {person.name} — not verified by the platform.
          </p>
          {self.bio ? <p>{self.bio}</p> : null}
          <dl>
            {/* PR-F25 / UR-F16: a competency level must always read "self-assessed". */}
            <Field label="Competency (self-assessed)" value={self.competencyLevel} />
            <Field label="Maker type (self-declared)" value={self.makerType} />
          </dl>
          <Pills label="Interests" items={self.interests} />
          {/* UR-F31: both lists are public. Only "skills they have" is searchable — see peopleSearch.ts. */}
          <Pills label="Skills they have (self-declared)" items={self.skillsHave} />
          <Pills label="Skills they want to build (self-declared)" items={self.skillsWanted} />
          {self.certifications.length > 0 ? (
            <>
              <h3>Certifications (self-declared, not verified)</h3>
              <ul>
                {self.certifications.map((c) => (
                  <li key={`${c.name}-${c.year}`}>
                    {c.name} — {c.year}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </section>
      ) : null}

      {/*
        Badges (PR-F43): every badge the person has earned and not hidden, shown as accent chips with
        the masterlist criterion as hover text — reusing the SAME `.tb-profile__achievements` style the
        owner's page uses for their own strip. The featured few show first; "View more" reveals the
        rest inline (W3-people pattern). LABELS ONLY, never a count (PC-F31 — see the file header).
        `[]` means "earned none, hid all, OR opted out globally"; PersonBadges omits the whole section
        then, exactly like About, so an empty heading never advertises that something was hidden.
      */}
      <PersonBadges badges={person.badges} />

      {/*
        The contribution summary (UR-F21/PR-F27/PR-F56) — the figures THEY chose, beside the badges
        because both answer "what has this person done?". `?? []` because the mirror this renders from is
        hand-written: an API that does not carry the field must show no summary, not throw.
      */}
      <PersonContributionFigures figures={person.contributionFigures ?? []} />

      {/*
        The learning THEY chose to show (PR-F30) — after the figures because "Learning completed" is one
        of those six, and the server (not this page) decides when publishing both would let the figure
        undo a per-step hide. `?? []` for the same hand-written-mirror reason as above. Absent for almost
        everyone: nothing learning-related is public until they choose it.
      */}
      <PersonChosenLearning steps={person.learningSteps ?? []} />

      {/*
        NOTHING ELSE APPEARS HERE, and that is the point. Saved tools, adopted tools, submissions,
        support requests, objectives, challenge progress, who they follow and anything about ratings they
        have given are owner-only or shown to nobody (§3.2, PR-F05, PR-F47). They are not fetched, so
        they cannot be rendered by mistake.
      */}
    </section>
  );
}
